import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Chip,
  Grid,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const PricingSection = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  /* ---------------- TAWK OPEN HANDLER ---------------- */

  const openTawkChat = () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
    if (window.Tawk_API && window.Tawk_API.maximize) {
      window.Tawk_API.maximize();
    }
  };
  const handlePlanAction = (planName) => {
    if (planName === 'Free Trial') {
      window.location.href = '/login';
      return;
    }

    if (planName === 'Pro' || planName === 'Enterprise') {
      openTawkChat();
    }
  };

  const plans = [
    {
      name: 'Free Trial',
      price: '$0',
      period: '5 day trial',
      description: 'Perfect for testing our platform',
      features: [
        '5 Credits',
        'Basic candidate insights',
      ],
      button: 'Try Now – Free',
      variant: 'outlined',
    },
    {
      name: 'Pro',
      price: isAnnual ? '$1100' : '$100',
      period: isAnnual ? 'per year' : 'per month',
      description: 'For growing teams and recruiters',
      features: isAnnual
        ? [
            '990 Credits',
            '365 Days Access',
            'Unlimited Searches',
            'Unlimited Profile Views',
            '365 Days AI Insights',
            '365 Days Lists Access',
            'Priority Support',
            'Advanced Analytics',
          ]
        : [
            '90 Credits',
            '30 Days Access',
            'Unlimited Searches',
            'Unlimited Profile Views',
            '30 Days AI Insights',
            '30 Days Lists Access',
            'Priority Support',
            'Advanced Analytics',
          ],
      button: 'Book a Demo',
      variant: 'contained',
      popular: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'Contact for pricing',
      description: 'For large organizations',
      features: [
        'Custom Duration',
        'Unlimited AI Insights',
        'Unlimited Lists Access',
        'Custom integrations',
        'Team Management',
        'API',
      ],
      button: 'Contact Sales',
      variant: 'outlined',
    },
  ];

  return (
    <Box id="pricing" component="section" sx={{ py: 10, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        {/* HEADER */}
        <Box textAlign="center" mb={6}>
          <Typography variant="h2" fontWeight={800} mb={1.5}>
            Simple, Transparent Pricing
          </Typography>
          <Typography color="text.secondary" fontSize={17} mb={4}>
            Start with a free trial and scale as you grow. No hidden fees, no long-term commitments.
          </Typography>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Typography>Monthly</Typography>
            <Switch
              checked={isAnnual}
              onChange={() => setIsAnnual(!isAnnual)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#22c55e' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  bgcolor: '#22c55e',
                },
              }}
            />
            <Typography>Annual</Typography>
            <Chip
              label="Save 8%"
              size="small"
              sx={{
                bgcolor: '#dcfce7',
                color: '#065f46',
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>

        {/* PRICING CARDS */}
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  borderRadius: 4,
                  border: plan.popular
                    ? '2px solid #22c55e'
                    : '1px solid #e5e7eb',
                  boxShadow: plan.popular ? 6 : 2,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                   overflow: 'visible',
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: '#22c55e',
                      color: '#fff',
                      px: 3,
                      py: 0.6,
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    Most Popular
                  </Box>
                )}

                <Typography variant="h5" fontWeight={700} mt={plan.popular ? 2 : 0}>
                  {plan.name}
                </Typography>

                <Typography color="text.secondary" mb={3}>
                  {plan.description}
                </Typography>

                <Box mb={3}>
                  <Typography variant="h3" fontWeight={800}>
                    {plan.price}
                  </Typography>
                  <Typography color="text.secondary" fontSize={14}>
                    {plan.period}
                  </Typography>
                </Box>

                <List sx={{ mb: 3 }}>
                  {plan.features.map((f, idx) => (
                    <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={f} />
                    </ListItem>
                  ))}
                </List>

                <Box mt="auto">
                  <Button
                    fullWidth
                    size="large"
                    variant={plan.variant}
                    onClick={() => handlePlanAction(plan.name)}
                    sx={{
                      py: 1.4,
                      fontWeight: 600,
                      ...(plan.variant === 'outlined' && {
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          bgcolor: '#f0fdf4',
                        },
                      }),
                    }}
                  >
                    {plan.button}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingSection;